import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { CreditCard, DollarSign, Clock, Package } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function PrepaidRentalDialog({ unit, isOpen, onClose, onSuccess }) {
  const [customerName, setCustomerName] = useState(''); // Nama customer
  const [customerPhone, setCustomerPhone] = useState(''); // Nomor HP customer (optional)
  const [rentalMode, setRentalMode] = useState('durasi'); // 'paket' or 'durasi'
  const [duration, setDuration] = useState(1); // Default 1 jam
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [packages, setPackages] = useState([]);
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [amountPaid, setAmountPaid] = useState(''); // Uang yang dibayar customer
  const [loading, setLoading] = useState(false);
  
  // F&B State
  const [fnbProducts, setFnbProducts] = useState([]);
  const [selectedFnbItems, setSelectedFnbItems] = useState([]); // [{product_id, name, price, qty}]
  const [fnbSearchTerm, setFnbSearchTerm] = useState(''); // Search F&B products

  const pricePerHour = unit?.price_per_hour || 5000;
  
  // Calculate rental base price
  const rentalBasePrice = rentalMode === 'paket' 
    ? (selectedPackage?.price || 0)
    : (duration * pricePerHour);
  
  // Calculate F&B total
  const fnbTotal = selectedFnbItems.reduce((sum, item) => sum + (item.price * item.qty), 0);
  
  // Calculate total price (rental + F&B)
  const totalPrice = rentalBasePrice + fnbTotal;
  
  // Calculate change
  const change = amountPaid ? Math.max(0, parseInt(amountPaid) - totalPrice) : 0;

  // Filter F&B products based on search term
  const filteredFnbProducts = fnbProducts.filter(product =>
    product.name.toLowerCase().includes(fnbSearchTerm.toLowerCase()) ||
    product.category?.toLowerCase().includes(fnbSearchTerm.toLowerCase())
  );

  // Fetch packages and F&B products on mount
  useEffect(() => {
    if (isOpen) {
      fetchPackages();
      fetchFnbProducts();
    }
  }, [isOpen]);

  const fetchPackages = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/packages`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPackages(response.data || []);
    } catch (error) {
      console.error('Error fetching packages:', error);
    }
  };

  const fetchFnbProducts = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/products`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Ensure we always set an array from correct response structure
      const products = Array.isArray(response.data?.products) ? response.data.products : [];
      setFnbProducts(products);
    } catch (error) {
      console.error('Error fetching F&B products:', error);
      setFnbProducts([]); // Set empty array on error
    }
  };

  // Add F&B item
  const addFnbItem = (product) => {
    const existingItem = selectedFnbItems.find(item => item.product_id === product.id);
    
    if (existingItem) {
      // Increment quantity
      setSelectedFnbItems(selectedFnbItems.map(item =>
        item.product_id === product.id 
          ? { ...item, qty: item.qty + 1 }
          : item
      ));
    } else {
      // Add new item
      setSelectedFnbItems([...selectedFnbItems, {
        product_id: product.id,
        name: product.name,
        price: product.price,
        qty: 1
      }]);
    }
  };

  // Remove F&B item
  const removeFnbItem = (productId) => {
    const existingItem = selectedFnbItems.find(item => item.product_id === productId);
    
    if (existingItem && existingItem.qty > 1) {
      // Decrement quantity
      setSelectedFnbItems(selectedFnbItems.map(item =>
        item.product_id === productId 
          ? { ...item, qty: item.qty - 1 }
          : item
      ));
    } else {
      // Remove item completely
      setSelectedFnbItems(selectedFnbItems.filter(item => item.product_id !== productId));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate customer name
    if (!customerName || customerName.trim() === '') {
      toast.error(' Nama customer harus diisi!');
      return;
    }
    
    // Validate payment for cash method
    if (paymentMethod === 'cash') {
      if (!amountPaid || parseInt(amountPaid) < totalPrice) {
        toast.error(' Uang yang dibayar kurang dari total harga!');
        return;
      }
    }
    
    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      
      // Build payload based on rental mode
      const payload = {
        plug_id: unit.id,
        customer_name: customerName.trim(),
        customer_phone: customerPhone.trim(),
        rental_mode: rentalMode,
        prepaid: true,
        payment_method: paymentMethod,
        additional_items: selectedFnbItems.map(item => ({
          product_id: item.product_id,
          name: item.name,
          price: item.price,
          qty: item.qty
        }))
      };

      if (rentalMode === 'paket') {
        payload.package_id = selectedPackage.id;
        payload.package_name = selectedPackage.name;
        payload.package_price = selectedPackage.price;
        payload.prepaid_duration = selectedPackage.duration_hours;
      } else {
        payload.prepaid_duration = duration;
      }

      // Add cash payment details
      if (paymentMethod === 'cash') {
        payload.amount_paid = parseInt(amountPaid);
        payload.change = change;
      }
      
      // Start rental dengan prepaid flag
      const response = await axios.post(
        `${API_URL}/api/rentals/start`,
        payload,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (response.data.success) {
        const durationText = rentalMode === 'paket' 
          ? selectedPackage.name 
          : `${duration} jam`;
        
        // Show change if cash payment
        if (paymentMethod === 'cash' && change > 0) {
          toast.success(` Pembayaran Berhasil! Kembalian: Rp ${change.toLocaleString('id-ID')}`);
        } else {
          toast.success(` Pembayaran Berhasil! Rental dimulai untuk ${durationText}`);
        }
        
        onSuccess();
        onClose();
        
        // Reset form
        setCustomerName('');
        setCustomerPhone('');
        setRentalMode('durasi');
        setDuration(1);
        setSelectedPackage(null);
        setPaymentMethod('cash');
        setAmountPaid('');
        setSelectedFnbItems([]);
        setFnbSearchTerm('');
      }
    } catch (error) {
      console.error('Error starting prepaid rental:', error);
      toast.error(error.response?.data?.detail || 'Gagal memulai rental prepaid');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto scrollbar-hide">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CreditCard className="h-5 w-5 text-blue-500" />
            Bayar di Awal - {unit?.name}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 py-4">
          {/* Customer Info */}
          <div className="space-y-2">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                 Nama Customer *
              </label>
              <input
                type="text"
                value={customerName}
                onChange={(e) => setCustomerName(e.target.value)}
                className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
                placeholder="Masukkan nama customer"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                 Nomor HP (Opsional)
              </label>
              <input
                type="tel"
                value={customerPhone}
                onChange={(e) => setCustomerPhone(e.target.value)}
                className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg focus:border-gray-400 focus:ring-2 focus:ring-gray-200"
                placeholder="Contoh: 08123456789"
              />
            </div>
          </div>

          {/* Mode Rental */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Mode Rental
            </label>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => setRentalMode('paket')}
                className={`py-2 px-3 rounded-lg font-semibold transition flex items-center justify-center gap-2 ${
                  rentalMode === 'paket'
                    ? 'bg-purple-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                <Package className="h-4 w-4" />
                Paket
              </button>
              <button
                type="button"
                onClick={() => setRentalMode('durasi')}
                className={`py-2 px-3 rounded-lg font-semibold transition flex items-center justify-center gap-2 ${
                  rentalMode === 'durasi'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                <Clock className="h-4 w-4" />
                Durasi
              </button>
            </div>
          </div>

          {/* Paket Selection */}
          {rentalMode === 'paket' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <Package className="inline h-4 w-4 mr-1" />
                Pilih Paket
              </label>
              <div className="space-y-1 max-h-32 overflow-y-auto">
                {packages.map((pkg) => (
                  <button
                    key={pkg.id}
                    type="button"
                    onClick={() => setSelectedPackage(pkg)}
                    className={`w-full p-2 rounded-lg border-2 transition text-left ${
                      selectedPackage?.id === pkg.id
                        ? 'border-purple-600 bg-purple-50'
                        : 'border-gray-200 hover:border-purple-300'
                    }`}
                  >
                    <div className="flex justify-between items-center">
                      <div>
                        <p className="font-semibold text-sm text-gray-900">{pkg.name}</p>
                        <p className="text-xs text-gray-600">{pkg.duration_hours} jam</p>
                      </div>
                      <p className="text-sm font-bold text-purple-600">
                        Rp {pkg.price.toLocaleString('id-ID')}
                      </p>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Durasi Selection (only if durasi mode) */}
          {rentalMode === 'durasi' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <Clock className="inline h-4 w-4 mr-1" />
                Durasi Rental
              </label>
              <div className="grid grid-cols-4 gap-2">
                {[1, 2, 3, 4].map((hour) => (
                  <button
                    key={hour}
                    type="button"
                    onClick={() => setDuration(hour)}
                    className={`py-2 px-2 rounded-lg font-semibold transition ${
                      duration === hour
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {hour} Jam
                  </button>
                ))}
              </div>
              <div className="mt-2">
                <input
                  type="number"
                  min="1"
                  max="24"
                  value={duration}
                  onChange={(e) => setDuration(parseInt(e.target.value) || 1)}
                  className="w-full px-3 py-1 border border-gray-300 rounded-lg text-sm"
                  placeholder="Manual (jam)"
                />
              </div>
            </div>
          )}

          {/* F&B Items */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
               Tambah F&B (Opsional)
            </label>

            {/* Search F&B */}
            <div className="mb-2">
              <input
                type="text"
                value={fnbSearchTerm}
                onChange={(e) => setFnbSearchTerm(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:border-orange-500 focus:ring-2 focus:ring-orange-200"
                placeholder=" Cari produk F&B... (nama/kategori)"
              />
            </div>
            
            {/* Selected F&B Items */}
            {selectedFnbItems.length > 0 && (
              <div className="mb-2 p-2 bg-orange-50 border border-orange-200 rounded-lg space-y-1">
                {selectedFnbItems.map((item) => (
                  <div key={item.product_id} className="flex justify-between items-center">
                    <div>
                      <p className="font-semibold text-sm text-gray-800">{item.name}</p>
                      <p className="text-xs text-gray-600">
                        Rp {item.price.toLocaleString('id-ID')} × {item.qty}
                      </p>
                    </div>
                    <div className="flex items-center gap-1">
                      <button
                        type="button"
                        onClick={() => removeFnbItem(item.product_id)}
                        className="px-2 py-1 bg-red-100 hover:bg-red-200 rounded text-red-600 font-bold text-xs"
                      >
                        -
                      </button>
                      <span className="font-bold text-gray-800 text-sm">{item.qty}</span>
                      <button
                        type="button"
                        onClick={() => addFnbItem({ id: item.product_id, name: item.name, price: item.price })}
                        className="px-2 py-1 bg-green-100 hover:bg-green-200 rounded text-green-600 font-bold text-xs"
                      >
                        +
                      </button>
                    </div>
                  </div>
                ))}
                <div className="pt-1 border-t border-orange-300">
                  <div className="flex justify-between font-bold text-orange-700 text-sm">
                    <span>Total F&B:</span>
                    <span>Rp {fnbTotal.toLocaleString('id-ID')}</span>
                  </div>
                </div>
              </div>
            )}

            {/* F&B Product List */}
            <div className="grid grid-cols-2 gap-1 max-h-24 overflow-y-auto scrollbar-hide">
              {Array.isArray(filteredFnbProducts) && filteredFnbProducts.length > 0 ? (
                filteredFnbProducts.map((product) => (
                  <button
                    key={product.id}
                    type="button"
                    onClick={() => addFnbItem(product)}
                    className="p-2 text-left bg-gray-50 hover:bg-orange-100 border border-gray-200 hover:border-orange-300 rounded-lg transition"
                  >
                    <p className="font-semibold text-xs text-gray-800">{product.name}</p>
                    <p className="text-xs text-orange-600 font-semibold">
                      Rp {product.price.toLocaleString('id-ID')}
                    </p>
                    <p className="text-xs text-gray-500">{product.category}</p>
                  </button>
                ))
              ) : (
                <p className="col-span-2 text-center text-gray-500 text-xs py-2">
                  {fnbSearchTerm ? `Tidak ditemukan "${fnbSearchTerm}"` : "Tidak ada produk F&B"}
                </p>
              )}
            </div>

            {/* Search Results Info */}
            {fnbSearchTerm && (
              <p className="text-xs text-gray-500 mt-1">
                {filteredFnbProducts.length} produk ditemukan dari {fnbProducts.length} total produk
              </p>
            )}
          </div>

          {/* Metode Pembayaran */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              <DollarSign className="inline h-4 w-4 mr-1" />
              Metode Pembayaran
            </label>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => setPaymentMethod('cash')}
                className={`py-2 px-3 rounded-lg font-semibold transition ${
                  paymentMethod === 'cash'
                    ? 'bg-green-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                 Tunai
              </button>
              <button
                type="button"
                onClick={() => setPaymentMethod('qris')}
                className={`py-2 px-3 rounded-lg font-semibold transition ${
                  paymentMethod === 'qris'
                    ? 'bg-purple-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                 QRIS
              </button>
            </div>
          </div>

          {/* Input Uang (only for cash payment) */}
          {paymentMethod === 'cash' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                 Uang yang Dibayar
              </label>
              <input
                type="number"
                value={amountPaid}
                onChange={(e) => setAmountPaid(e.target.value)}
                className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg text-lg font-semibold focus:border-green-500 focus:ring-2 focus:ring-green-200"
                placeholder="Masukkan jumlah uang"
                required
              />
              
              {/* Quick Amount Buttons + Uang Pas */}
              <div className="grid grid-cols-5 gap-1 mt-2">
                <button
                  type="button"
                  onClick={() => setAmountPaid(totalPrice.toString())}
                  className="py-1 px-2 text-xs bg-blue-500 hover:bg-blue-600 text-white rounded-lg font-semibold transition"
                >
                   Pas
                </button>
                {[50000, 100000, 150000, 200000].map((amount) => (
                  <button
                    key={amount}
                    type="button"
                    onClick={() => setAmountPaid(amount.toString())}
                    className="py-1 px-2 text-xs bg-gray-100 hover:bg-gray-200 rounded-lg font-semibold transition"
                  >
                    {(amount / 1000)}K
                  </button>
                ))}
              </div>

              {/* Show change if paid amount is sufficient */}
              {amountPaid && parseInt(amountPaid) >= totalPrice && (
                <div className="mt-2 p-2 bg-green-50 border-2 border-green-300 rounded-lg">
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-medium text-green-700">Kembalian</span>
                    <span className="text-lg font-bold text-green-600">
                      Rp {change.toLocaleString('id-ID')}
                    </span>
                  </div>
                </div>
              )}

              {/* Warning if insufficient */}
              {amountPaid && parseInt(amountPaid) < totalPrice && (
                <div className="mt-2 p-2 bg-red-50 border-2 border-red-300 rounded-lg">
                  <p className="text-sm font-medium text-red-600">
                     Uang kurang Rp {(totalPrice - parseInt(amountPaid)).toLocaleString('id-ID')}
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Total Harga */}
          <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-3">
            <div className="flex justify-between items-center">
              <div>
                <p className="text-sm text-gray-600">Total Pembayaran</p>
                <p className="text-xs text-gray-500 mt-1">
                  {rentalMode === 'paket' 
                    ? selectedPackage?.name 
                    : `${duration} jam × Rp ${pricePerHour.toLocaleString('id-ID')}`}
                </p>
              </div>
              <div className="text-right">
                <p className="text-xl font-bold text-blue-600">
                  Rp {totalPrice.toLocaleString('id-ID')}
                </p>
              </div>
            </div>
          </div>

          {/* Buttons */}
          <div className="flex gap-2 pt-2">
            <Button
              type="button"
              onClick={onClose}
              variant="outline"
              className="flex-1"
              disabled={loading}
            >
              Batal
            </Button>
            <Button
              type="submit"
              className="flex-1 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
              disabled={loading}
            >
              {loading ? 'Memproses...' : `Bayar & Mulai`}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
